import { calculateZoomLevel } from '@lexical/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'

let skipAddingToHistoryStack = false

const basicColors = [
  '#d0021b',
  '#f5a623',
  '#f8e71c',
  '#8b572a',
  '#7ed321',
  '#417505',
  '#bd10e0',
  '#9013fe',
  '#4a90e2',
  '#50e3c2',
  '#b8e986',
  '#000000',
  '#4a4a4a',
  '#9b9b9b',
  '#ffffff'
]

const WIDTH = 214
const HEIGHT = 150


interface RGB {
  r: number
  g: number
  b: number
}

interface HSV {
  h: number
  s: number
  v: number
}

interface Color {
  hex: string
  hsv: HSV
  rgb: RGB
}

export default function ColorPicker({ color, onChange }: { color: string; onChange?: (color: string, skipHistory?: boolean) => void }): React.JSX.Element {
  const [selfColor, setSelfColor] = useState<Color>(transformColor('hex', color))
  const [inputColor, setInputColor] = useState(color)
  const innerDivRef = useRef<HTMLDivElement>(null)

  const saturationPosition = useMemo(
    () => ({
      x: (selfColor.hsv.s / 100) * WIDTH,
      y: ((100 - selfColor.hsv.v) / 100) * HEIGHT
    }),
    [selfColor.hsv.s, selfColor.hsv.v]
  )

  const huePosition = useMemo(
    () => ({
      x: (selfColor.hsv.h / 360) * WIDTH
    }),
    [selfColor.hsv.h]
  )

  const onSetHex = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value
    setInputColor(hex)
    if (/^#[0-9A-Fa-f]{6}$/i.test(hex)) {
      const newColor = transformColor('hex', hex)
      setSelfColor(newColor)
      if (onChange) onChange(newColor.hex, skipAddingToHistoryStack)
    }
  }

  const onMoveSaturation = ({ x, y }: { x: number; y: number }) => {
    const newHsv = {
      ...selfColor.hsv,
      s: (x / WIDTH) * 100,
      v: 100 - (y / HEIGHT) * 100
    }
    const newColor = transformColor('hsv', newHsv)
    setSelfColor(newColor)
    setInputColor(newColor.hex)
    if (onChange) onChange(newColor.hex, skipAddingToHistoryStack)
  }

  const onMoveHue = ({ x }: { x: number }) => {
    const newHsv = { ...selfColor.hsv, h: (x / WIDTH) * 360 }
    const newColor = transformColor('hsv', newHsv)

    setSelfColor(newColor)
    setInputColor(newColor.hex)
    if (onChange) onChange(newColor.hex, skipAddingToHistoryStack)
  }

  useEffect(() => {
    if (color === undefined) {
      return
    }
    const newColor = transformColor('hex', color)
    setSelfColor(newColor)
    setInputColor(newColor.hex)
  }, [color])

  return (
    <div className='w-full flex items-center justify-center'>
      <div className='p-2' style={{ width: WIDTH }} ref={innerDivRef}>
        <div className='flex items-center gap-2'>
          <label className='text-sm'>{'Hex'}</label>
          <input
            onChange={onSetHex}
            value={inputColor}
            placeholder={''}
            type='text'
            className='focus:outline-none focus:ring-1 focus:ring-blue-500 border-gray-300 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500'
          />
        </div>

        <div className='flex flex-wrap gap-2 m-0 p-0 mt-3'>
          {basicColors.map((basicColor) => (
            <button
              className={`${basicColor === selfColor.hex ? ' ring-2 ring-blue-500' : ''
                } border border-gray-200 rounded h-5 w-5 cursor-pointer list-none`}
              key={basicColor}
              style={{ backgroundColor: basicColor }}
              onClick={() => {
                setInputColor(basicColor)
                setSelfColor(transformColor('hex', basicColor))
                if (onChange) onChange(basicColor, skipAddingToHistoryStack)
              }}
            />
          ))}
        </div>
        <MoveWrapper
          className='w-full relative mt-3 h-[150px] bg-gray-200 select-none rounded overflow-hidden'
          style={{ backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`, backgroundImage: 'linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)' }}
          onChange={onMoveSaturation}
        >
          <div
            className='absolute w-4 h-4 border-2 border-white rounded-full shadow-md -translate-x-2 -translate-y-2 pointer-events-none'
            style={{
              backgroundColor: selfColor.hex,
              left: saturationPosition.x,
              top: saturationPosition.y
            }}
          />
        </MoveWrapper>
        <MoveWrapper
          className='w-full relative mt-6 h-3 select-none rounded-lg'
          style={{ backgroundImage: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)' }}
          onChange={onMoveHue}
        >
          <div
            className='absolute w-2 h-4 border-2 border-white bg-white rounded shadow-md -top-0.5 -translate-x-1 pointer-events-none'
            style={{
              left: huePosition.x
            }}
          />
        </MoveWrapper>
      </div>
    </div>
  )
}


function MoveWrapper({
  className,
  style,
  onChange,
  children
}: {
  className?: string
  style?: React.CSSProperties
  onChange: (pos: { x: number; y: number }) => void
  children: React.ReactNode
}) {
  const divRef = useRef<HTMLDivElement>(null)
  const draggedRef = useRef(false)

  const move = (e: React.MouseEvent | MouseEvent) => {
    if (divRef.current) {
      const { current: div } = divRef
      const { width, height, left, top } = div.getBoundingClientRect()
      const zoom = calculateZoomLevel(div)
      const x = clamp((e as MouseEvent).clientX / zoom - left, width, 0)
      const y = clamp((e as MouseEvent).clientY / zoom - top, height, 0)

      onChange({ x, y })
    }
  }

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) {
      return
    }

    move(e)

    const onMouseMove = (_e: MouseEvent) => {
      draggedRef.current = true
      skipAddingToHistoryStack = true
      move(_e)
    }

    const onMouseUp = (_e: MouseEvent) => {
      if (draggedRef.current) {
        skipAddingToHistoryStack = false
      }

      document.removeEventListener('mousemove', onMouseMove, false)
      document.removeEventListener('mouseup', onMouseUp, false)

      move(_e)
      draggedRef.current = false
    }

    document.addEventListener('mousemove', onMouseMove, false)
    document.addEventListener('mouseup', onMouseUp, false)
  }

  return (
    <div ref={divRef} className={className} style={style} onMouseDown={onMouseDown}>
      {children}
    </div>
  )
}

function clamp(value: number, max: number, min: number) {
  return value > max ? max : value < min ? min : value
}

export function toHex(value: string) {
  if (!value.startsWith('#')) {
    const ctx = document.createElement('canvas').getContext('2d')

    if (!ctx) {
      throw new Error('2d context not supported or canvas already initialized')
    }

    ctx.fillStyle = value

    return ctx.fillStyle
  } else if (value.length === 4 || value.length === 5) {
    value = value
      .split('')
      .map((v, i) => (i ? v + v : '#'))
      .join('')

    return value
  } else if (value.length === 7 || value.length === 9) {
    return value
  }

  return '#000000'
}

function hex2rgb(hex: string): RGB {
  const rbgArr = (
    hex
      .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (_m, r, g, b) => '#' + r + r + g + g + b + b)
      .substring(1)
      .match(/.{2}/g) || []
  ).map((x) => parseInt(x, 16))

  return {
    b: rbgArr[2],
    g: rbgArr[1],
    r: rbgArr[0]
  }
}

function rgb2hsv({ r, g, b }: RGB): HSV {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const d = max - Math.min(r, g, b)

  const h = d
    ? (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? 2 + (b - r) / d : 4 + (r - g) / d) *
    60
    : 0
  const s = max ? (d / max) * 100 : 0
  const v = max * 100

  return { h, s, v }
}

function hsv2rgb({ h, s, v }: HSV): RGB {
  s /= 100
  v /= 100

  const i = ~~(h / 60)
  const f = h / 60 - i
  const p = v * (1 - s)
  const q = v * (1 - s * f)
  const t = v * (1 - s * (1 - f))
  const index = i % 6

  const r = Math.round([v, q, p, p, t, v][index] * 255)
  const g = Math.round([t, v, v, q, p, p][index] * 255)
  const b = Math.round([p, p, t, v, v, q][index] * 255)

  return { b, g, r }
}

function rgb2hex({ b, g, r }: RGB): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')
}

function transformColor(format: 'hex' | 'rgb' | 'hsv', color: string | RGB | HSV): Color {
  let hex = toHex('#121212')
  let rgb = hex2rgb(hex)
  let hsv = rgb2hsv(rgb)

  if (format === 'hex') {
    const value = color as string

    hex = toHex(value)
    rgb = hex2rgb(hex)
    hsv = rgb2hsv(rgb)
  } else if (format === 'rgb') {
    const value = color as RGB

    rgb = value
    hex = rgb2hex(rgb)
    hsv = rgb2hsv(rgb)
  } else if (format === 'hsv') {
    const value = color as HSV

    hsv = value
    rgb = hsv2rgb(hsv)
    hex = rgb2hex(rgb)
  }

  return { hex, hsv, rgb }
}
